import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "@/i18n/translations";
import { sections } from "@/i18n/sections";
import { notableUI } from "@/i18n/notable";
import { heritage } from "@/i18n/heritage";
import { extraLocales, ALL_LANGUAGES, LANG_DIR, LANG_SCRIPT } from "@/i18n/locales";
import { getRecipes } from "@/i18n/recipes_locales";
import { ensureScriptFont } from "@/i18n/fonts";

const SUPPORTED = ALL_LANGUAGES.map((l) => l.code);
const STORAGE_KEY = "clan-donald-lang";

// Map some common browser regional tags onto our language codes.
const ALIASES = {
  "zh-cn": "zh", "zh-sg": "zh", "zh-hans": "zh",
  "zh-tw": "zh-Hant", "zh-hk": "zh-Hant", "zh-mo": "zh-Hant", "zh-hant": "zh-Hant",
  "nb": "no", "nn": "no",
};

const LanguageContext = createContext(null);

// Build the merged translation tree for a language (core files or extra locales).
function getTree(lang) {
  if (translations[lang]) {
    return { ...translations[lang], ...sections[lang], ...heritage[lang], notable: notableUI[lang], recipes: getRecipes(lang) };
  }
  const loc = extraLocales[lang];
  return { ...loc.translations, ...loc.sections, ...loc.heritage, notable: loc.notable, recipes: getRecipes(lang) };
}

// Auto-detect the visitor's language: exact regional tag first, then base.
function detectBrowserLang() {
  if (typeof navigator === "undefined") return "en";
  const candidates = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
  for (const c of candidates) {
    const lower = c.toLowerCase();
    if (ALIASES[lower]) return ALIASES[lower];
    const exact = SUPPORTED.find((s) => s.toLowerCase() === lower);
    if (exact) return exact;
  }
  for (const c of candidates) {
    const base = c.toLowerCase().split("-")[0];
    if (ALIASES[base]) return ALIASES[base];
    if (SUPPORTED.includes(base)) return base;
  }
  return "en";
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState("en");
  const [autoDetected, setAutoDetected] = useState(false);

  useEffect(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (stored && SUPPORTED.includes(stored)) {
      setLang(stored);
    } else {
      const detected = detectBrowserLang();
      setLang(detected);
      if (detected !== "en") setAutoDetected(true);
    }
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.lang = lang;
    root.dir = LANG_DIR[lang] || "ltr";
    const script = LANG_SCRIPT[lang] || "latin";
    root.setAttribute("data-script", script);
    ensureScriptFont(script);
  }, [lang]);

  const changeLang = (code) => {
    if (!SUPPORTED.includes(code)) return;
    setLang(code);
    setAutoDetected(false);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, code);
  };

  const value = useMemo(
    () => ({
      lang,
      t: getTree(lang),
      changeLang,
      languages: ALL_LANGUAGES,
      autoDetected,
      dismissAutoNotice: () => setAutoDetected(false),
    }),
    [lang, autoDetected]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
